import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
    AcademicCapIcon,
    CalendarDaysIcon,
    PhotoIcon,
    UserGroupIcon,
    VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { DrawerNoHeader } from '../../components/drawer/DrawerNoHeader';
import { Button } from '../../components/buttons/Button';
import { SearchableSelect } from '../../components/searchableSelect';
import { ClassDaysPickerModern, TimePickerModern } from '../../components/timePickerModern';
import {
    buildSlotRange,
    rangeFromSlots,
    snapToDayTimeSlot,
    snapToNearestSlot,
} from '../../components/timePickerModern/timePickerUtils';
import { ColorTagPickerModern } from '../../components/colorTagPickerModern';
import { InputLimit } from '../../components/inputLimit/InputLimit';
import { getNameInitials } from '../../common/nameInitials';
import { urlFlag } from '../../common/urlBase';
import { InputTextModern } from '../../components/inputs/InputTextModern';
import { InputDateModern } from '../../components/inputDateModern/InputDateModern';
import { InputSwitch } from '../../components/inputSwitch/InputSwitch';
import { BannerImageCarousel } from './components/BannerImageCarousel';
import { CourseFormGrid, CourseFormSection } from './components/CourseFormSection';
import { CourseStatusBadge } from './components/CourseStatusBadge';
import { statusCourses } from '../../static/data';
import { canToggleCourseZoom } from '../../constants/courseZoom';
import { canManageCourses } from '../../constants/courseManage';
import {
    optionsAllAdminUser,
    optionsAllTeachers,
    optionsColors,
    optionsLanguages,
    optionsLevels,
} from '../../redux/actions/options';
import {
    cleanActionSelectedCourse,
    createCourse,
    getCourseById,
    updateCourse,
} from '../../redux/actions/course';
import { getAllHeadersImages } from '../../redux/actions/setting';
import {
    buildCourseValidationToastMessage,
    COURSE_FORM_FIELD_LABELS,
    createEmptyCourseFormErrors,
    validateCourseFormValues,
} from './utils/courseFormValidation';

const courseStatusAvoid = ['completado', 'cancelado'];

const buildPersonSelectOptions = (items, subtitle, selectedPerson) => {
    const source = Array.isArray(items) ? items : [];
    const options = source.map(({ _id, name, email }) => ({
        value: String(_id),
        label: name || 'Sin nombre',
        initials: getNameInitials(name),
        subtitle,
        meta: typeof email === 'string' ? email : '',
    }));

    const selectedId = selectedPerson?._id ? String(selectedPerson._id) : '';
    if (selectedId && !options.some((option) => option.value === selectedId)) {
        options.unshift({
            value: selectedId,
            label: selectedPerson.name,
            initials: getNameInitials(selectedPerson.name),
            subtitle,
            meta: selectedPerson.email || '',
        });
    }

    return options;
};

const mapCourseToForm = (course, courseId) => ({
    id: courseId || '',
    identifier: course?.identifier || '',
    language: course?.language || '',
    level: course?.level || '',
    color: course?.color || '',
    limitMembers: course?.limitMembers ?? 0,
    hours: course?.hours || [],
    days: course?.days || [],
    fromDate: course?.fromDate ? new Date(course.fromDate) : null,
    toDate: course?.toDate ? new Date(course.toDate) : null,
    teacher: course?.teacher ? {
        _id: course.teacher._id,
        name: `${course.teacher.firstName} ${course.teacher.lastName}`,
        description: `${course.teacher.firstName} ${course.teacher.lastName}`,
        email: course.teacher.email || '',
    } : '',
    moderator: course?.moderator ? {
        _id: course.moderator._id,
        name: `${course.moderator.firstName} ${course.moderator.lastName}`,
        description: `${course.moderator.firstName} ${course.moderator.lastName}`,
        email: course.moderator.email || '',
    } : '',
    headerImage: course?.headerImage?._id || course?.headerImage || '',
    status: course?.status ? {
        _id: course.status,
        name: course.status,
        description: course.status,
    } : '',
    zoomEnabled: course?.zoomEnabled === true,
});

export const DrawerCourseForm = ({
    isOpen,
    onClose,
    courseId = null,
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const isEdit = Boolean(courseId);
    const { user } = useSelector((state) => state.user);
    const { courseSelected: course, loadingCreateEdit } = useSelector((state) => state.course);
    const headersImage = useSelector((state) => state.setting.headersImage);
    const { teachers, levels, colors, languages, adminUsers } = useSelector((state) => state.options);

    const [courseSelected, setCourseSelected] = useState(() => mapCourseToForm(null));
    const [headerImageSelected, setHeaderImageSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formErrors, setFormErrors] = useState(createEmptyCourseFormErrors);
    const [showValidationSummary, setShowValidationSummary] = useState(false);
    const [fieldsKey, setFieldsKey] = useState(0);

    const scrollContainerRef = useRef(null);
    const validationSummaryRef = useRef(null);
    const draftEntityKeyRef = useRef(null);
    const hydratedEditIdRef = useRef(null);

    const draftEntityKey = isEdit && courseId ? `edit:${courseId}` : 'create';

    const userCanManageCourses = canManageCourses(user?.typeUser);
    const canManageZoom = canToggleCourseZoom(user?.typeUser);

    const clearFieldError = useCallback((field) => {
        setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }, []);

    const resetValidationState = useCallback(() => {
        setFormErrors(createEmptyCourseFormErrors());
        setShowValidationSummary(false);
    }, []);

    const resetCreateDraft = useCallback(() => {
        dispatch(cleanActionSelectedCourse());
        setCourseSelected(mapCourseToForm(null));
        setHeaderImageSelected(null);
    }, [dispatch]);

    useEffect(() => {
        if (!isOpen) return;
        if (!userCanManageCourses) {
            toast.error('No tienes permiso para gestionar cursos.');
            onClose();
        }
    }, [isOpen, userCanManageCourses, onClose]);

    useEffect(() => {
        if (!isOpen) return;
        dispatch(optionsAllTeachers());
        dispatch(optionsLevels());
        dispatch(optionsColors());
        dispatch(optionsLanguages());
        dispatch(getAllHeadersImages());
        dispatch(optionsAllAdminUser());
    }, [dispatch, isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        resetValidationState();
        setFieldsKey((key) => key + 1);

        if (draftEntityKeyRef.current === draftEntityKey) {
            return;
        }

        draftEntityKeyRef.current = draftEntityKey;
        hydratedEditIdRef.current = null;

        if (isEdit && courseId) {
            dispatch(getCourseById(courseId));
        } else {
            resetCreateDraft();
        }
    }, [
        isOpen,
        isEdit,
        courseId,
        draftEntityKey,
        dispatch,
        resetValidationState,
        resetCreateDraft,
    ]);

    useEffect(() => {
        if (!isOpen || !isEdit || !course) return;
        if (courseStatusAvoid.includes(course?.status)) {
            toast.error('No se puede editar un curso completado o cancelado');
            draftEntityKeyRef.current = null;
            hydratedEditIdRef.current = null;
            onClose();
            return;
        }
        if (hydratedEditIdRef.current === String(course._id)) {
            return;
        }
        hydratedEditIdRef.current = String(course._id);
        setCourseSelected(mapCourseToForm(course, courseId));
        setHeaderImageSelected(course?.headerImage || null);
    }, [isOpen, isEdit, course, courseId, onClose]);

    useEffect(() => {
        if (!courseSelected) return;
        setCourseSelected((prev) => ({
            ...prev,
            headerImage: headerImageSelected?._id || prev?.headerImage,
        }));
    }, [headerImageSelected]);

    const levelOptions = useMemo(
        () => (levels || []).map(({ value, description }) => ({
            value: String(value),
            label: description,
        })),
        [levels],
    );

    const languageOptions = useMemo(
        () => (languages || []).map(({ value, description, path }) => ({
            value: String(value),
            label: description,
            image: path ? `${urlFlag}${path}` : undefined,
            imageRadius: 'sm',
        })),
        [languages],
    );

    const teacherOptions = useMemo(
        () => buildPersonSelectOptions(teachers, 'Profesor', courseSelected?.teacher),
        [teachers, courseSelected?.teacher],
    );

    const moderatorOptions = useMemo(
        () => buildPersonSelectOptions(adminUsers, 'Moderador', courseSelected?.moderator),
        [adminUsers, courseSelected?.moderator],
    );

    const statusOptions = useMemo(
        () => statusCourses.map(({ value, description }) => {
            const status = description || value;
            return {
                value,
                label: <CourseStatusBadge status={status} />,
            };
        }),
        [],
    );

    const statusValue = useMemo(() => {
        const status = courseSelected?.status;
        if (!status) return '';
        if (typeof status === 'object') {
            return String(status._id || status.name || status.description || '');
        }
        return String(status);
    }, [courseSelected?.status]);

    const isProcessing = saving || loadingCreateEdit;

    const handleDismiss = () => {
        if (isProcessing) return;
        resetValidationState();
        onClose();
    };

    const handleCancel = () => {
        if (isProcessing) return;
        draftEntityKeyRef.current = null;
        hydratedEditIdRef.current = null;
        setSaving(false);
        resetValidationState();
        resetCreateDraft();
        onClose();
    };

    const handleToggleDrawer = handleDismiss;

    const scheduleRange = useMemo(
        () => rangeFromSlots(courseSelected?.hours),
        [courseSelected?.hours],
    );
    const hasScheduleHours = Array.isArray(courseSelected?.hours) && courseSelected.hours.length > 0;
    const scheduleStartTime = hasScheduleHours
        ? snapToDayTimeSlot(scheduleRange.start)
        : '';
    const scheduleEndTime = hasScheduleHours
        ? snapToDayTimeSlot(scheduleRange.end)
        : '';

    const updateScheduleHours = useCallback((nextStart, nextEnd) => {
        clearFieldError('schedule');
        const startSlot = snapToNearestSlot(nextStart || scheduleRange.start || '08:00');
        let endSlot = snapToNearestSlot(nextEnd || scheduleRange.end || '09:00');
        if (endSlot.id < startSlot.id) {
            endSlot = startSlot;
        }
        const hours = buildSlotRange(startSlot.time, endSlot.time);
        setCourseSelected((prev) => ({
            ...prev,
            hours: hours.length ? hours : [startSlot],
        }));
    }, [clearFieldError, scheduleRange.end, scheduleRange.start]);

    const validateForm = useCallback(() => {
        const { isValid, errors } = validateCourseFormValues(courseSelected, headerImageSelected);
        setFormErrors(errors);

        if (!isValid) {
            setShowValidationSummary(true);
            const missingLabels = Object.entries(errors)
                .filter(([, msg]) => msg)
                .map(([key]) => COURSE_FORM_FIELD_LABELS[key]);
            toast.error(buildCourseValidationToastMessage(missingLabels), { duration: 5000 });
            requestAnimationFrame(() => {
                scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            });
            return false;
        }

        setShowValidationSummary(false);
        return true;
    }, [courseSelected, headerImageSelected]);

    const handleSubmit = async () => {
        if (!userCanManageCourses) {
            toast.error('No tienes permiso para gestionar cursos.');
            return;
        }
        if (!validateForm()) return;

        setSaving(true);
        const payload = {
            ...courseSelected,
            id: courseId,
            headerImage: headerImageSelected?._id || courseSelected?.headerImage,
            status: isEdit ? courseSelected?.status?._id || courseSelected?.status : undefined,
            zoomEnabled: canManageZoom ? courseSelected?.zoomEnabled === true : false,
        };

        try {
            const response = isEdit
                ? await dispatch(updateCourse(payload))
                : await dispatch(createCourse(payload));

            if (response?.status === 200 || response?.status === 201) {
                toast.success(isEdit ? 'Curso actualizado correctamente' : 'Curso creado correctamente');
                draftEntityKeyRef.current = null;
                hydratedEditIdRef.current = null;
                resetValidationState();
                resetCreateDraft();
                onSuccess?.();
                onClose();
            } else {
                toast.error(response?.message || 'No se pudo guardar el curso');
            }
        } catch {
            toast.error('Error al guardar el curso');
        } finally {
            setSaving(false);
        }
    };

    const drawerTitle = isEdit ? 'Editar curso' : 'Nuevo curso';
    const drawerSubtitle = isEdit ? courseSelected?.identifier : 'Completa los datos del curso';

    const validationErrorEntries = Object.entries(formErrors).filter(([, msg]) => msg);

    const validationSummary = showValidationSummary && validationErrorEntries.length > 0 ? (
        <div
            ref={validationSummaryRef}
            className="rounded-lg border border-red-200 bg-red-50 p-4"
            role="alert"
        >
            <p className="text-sm font-semibold text-red-800">
                {validationErrorEntries.length === 1
                    ? 'Falta 1 campo obligatorio'
                    : `Faltan ${validationErrorEntries.length} campos obligatorios`}
            </p>
            <p className="mt-1 text-sm text-red-700">
                Completa los siguientes campos para guardar el curso:
            </p>
            <ul className="mt-3 space-y-2 text-sm text-red-700">
                {validationErrorEntries.map(([key, msg]) => (
                    <li key={key} className="flex gap-2">
                        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" aria-hidden />
                        <span>
                            <span className="font-semibold text-red-800">
                                {COURSE_FORM_FIELD_LABELS[key]}
                            </span>
                            <span className="text-red-600">
                                {' — '}
                                {msg}
                            </span>
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    ) : null;

    const sectionIconClass = 'h-5 w-5';

    const drawerBody = (
        <div className="flex h-full w-full flex-col overflow-hidden bg-slate-50/80">
            <div className="flex-shrink-0 px-4 pt-4 sm:px-5">
                <header className="rounded-lg border border-gray-200 bg-white px-5 py-3 shadow-sm lg:py-4">
                    <div className="min-w-0">
                        <h1 className="truncate text-xl font-bold text-gray-900 text-pretty">{drawerTitle}</h1>
                        <p className="mt-0.5 truncate text-xs font-medium text-gray-500">
                            {drawerSubtitle || 'Completa los datos del curso'}
                        </p>
                    </div>
                </header>
            </div>

            <div
                ref={scrollContainerRef}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-5"
            >
                <div key={fieldsKey} className="flex w-full flex-col gap-5">
                    {validationSummary}

                    <CourseFormSection
                        id="course-section-banner"
                        title="Portada del curso"
                        description="La imagen principal que verán los alumnos al entrar al grupo."
                        icon={<PhotoIcon className={sectionIconClass} />}
                    >
                        <BannerImageCarousel
                            banners={headersImage}
                            selectedId={headerImageSelected?._id}
                            required
                            error={formErrors.headerImage}
                            onSelect={(banner) => {
                                clearFieldError('headerImage');
                                setHeaderImageSelected(banner);
                            }}
                        />
                    </CourseFormSection>

                    <CourseFormSection
                        id="course-section-identity"
                        title="Identidad del curso"
                        description="Nombre interno, periodo de vigencia, días y horario de clases."
                        icon={<CalendarDaysIcon className={sectionIconClass} />}
                    >
                        <InputTextModern
                            id="course-identifier"
                            name="identifier"
                            label="Identificador del curso"
                            onChange={(e) => {
                                clearFieldError('identifier');
                                setCourseSelected((prev) => ({ ...prev, identifier: e.target.value }));
                            }}
                            value={courseSelected?.identifier || ''}
                            placeholder="Ej. ING-A1-2026…"
                            required
                            error={formErrors.identifier}
                        />

                        <InputDateModern
                            id="course-date-range"
                            label="Duración del curso"
                            selectsRange
                            required
                            startDate={courseSelected?.fromDate}
                            endDate={courseSelected?.toDate}
                            onChange={(dates) => {
                                clearFieldError('duration');
                                const [fromDate, toDate] = dates || [null, null];
                                setCourseSelected((prev) => ({
                                    ...prev,
                                    fromDate,
                                    toDate,
                                }));
                            }}
                            placeholder="Selecciona inicio y fin del curso…"
                            popperPlacement="bottom-start"
                            error={formErrors.duration}
                        />

                        <div className="space-y-4">
                            <ClassDaysPickerModern
                                label="Días de clase"
                                required
                                value={courseSelected?.days || []}
                                onChange={(days) => {
                                    clearFieldError('schedule');
                                    setCourseSelected((prev) => ({
                                        ...prev,
                                        days,
                                    }));
                                }}
                                error={formErrors.schedule && !(courseSelected?.days || []).length
                                    ? formErrors.schedule
                                    : null}
                            />

                            <CourseFormGrid>
                                <TimePickerModern
                                    label="Hora de inicio"
                                    required
                                    daySlots
                                    value={scheduleStartTime}
                                    onChange={(value) => {
                                        updateScheduleHours(value, scheduleEndTime || value);
                                    }}
                                    placeholder="Hora"
                                    error={formErrors.schedule && !hasScheduleHours
                                        ? formErrors.schedule
                                        : null}
                                />
                                <TimePickerModern
                                    label="Hora de fin"
                                    required
                                    daySlots
                                    value={scheduleEndTime}
                                    onChange={(value) => {
                                        updateScheduleHours(scheduleStartTime || value, value);
                                    }}
                                    placeholder="Hora"
                                />
                            </CourseFormGrid>
                        </div>

                        {isEdit && (
                            <SearchableSelect
                                label="Estatus del curso"
                                options={statusOptions}
                                value={statusValue}
                                onChange={(nextValue) => {
                                    if (!nextValue) {
                                        setCourseSelected((prev) => ({ ...prev, status: '' }));
                                        return;
                                    }
                                    setCourseSelected((prev) => ({
                                        ...prev,
                                        status: {
                                            _id: nextValue,
                                            name: nextValue,
                                            description: nextValue,
                                        },
                                    }));
                                }}
                                placeholder="Selecciona el estatus del curso"
                                searchPlaceholder="Buscar estatus..."
                                maxVisibleOptions={5}
                            />
                        )}
                    </CourseFormSection>

                    <CourseFormSection
                        id="course-section-profile"
                        title="Perfil del grupo"
                        description="Clasificación académica, etiqueta visual y cupo."
                        icon={<AcademicCapIcon className={sectionIconClass} />}
                    >
                        <CourseFormGrid>
                            <SearchableSelect
                                label="Nivel"
                                required
                                options={levelOptions}
                                value={courseSelected?.level?._id ? String(courseSelected.level._id) : ''}
                                onChange={(nextValue) => {
                                    clearFieldError('level');
                                    if (!nextValue) {
                                        setCourseSelected((prev) => ({ ...prev, level: '' }));
                                        return;
                                    }
                                    const level = levels.find((item) => String(item.value) === nextValue);
                                    if (!level) return;
                                    setCourseSelected((prev) => ({
                                        ...prev,
                                        level: { _id: level.value, name: level.description },
                                    }));
                                }}
                                placeholder="Seleccionar nivel"
                                searchPlaceholder="Buscar nivel..."
                                error={formErrors.level}
                            />

                            <SearchableSelect
                                label="Idioma"
                                required
                                options={languageOptions}
                                value={courseSelected?.language?._id ? String(courseSelected.language._id) : ''}
                                onChange={(nextValue) => {
                                    clearFieldError('language');
                                    if (!nextValue) {
                                        setCourseSelected((prev) => ({ ...prev, language: '' }));
                                        return;
                                    }
                                    const language = languages.find((item) => String(item.value) === nextValue);
                                    if (!language) return;
                                    setCourseSelected((prev) => ({
                                        ...prev,
                                        language: {
                                            _id: language.value,
                                            name: language.description,
                                            path: language.path,
                                        },
                                    }));
                                }}
                                placeholder="Seleccionar idioma"
                                searchPlaceholder="Buscar idioma..."
                                error={formErrors.language}
                            />
                        </CourseFormGrid>

                        <CourseFormGrid>
                            <ColorTagPickerModern
                                label="Etiqueta de color"
                                required
                                colors={colors}
                                value={courseSelected?.color?._id ? String(courseSelected.color._id) : ''}
                                onChange={(nextColor) => {
                                    clearFieldError('color');
                                    setCourseSelected((prev) => ({
                                        ...prev,
                                        color: nextColor,
                                    }));
                                }}
                                error={formErrors.color}
                            />

                            <InputLimit
                                selected={courseSelected}
                                setSelected={setCourseSelected}
                                required
                                error={formErrors.limitMembers}
                                onChange={() => clearFieldError('limitMembers')}
                            />
                        </CourseFormGrid>
                    </CourseFormSection>

                    <CourseFormSection
                        id="course-section-team"
                        title="Equipo"
                        description="Profesor y moderador asignados al grupo."
                        icon={<UserGroupIcon className={sectionIconClass} />}
                    >
                        <CourseFormGrid>
                            <SearchableSelect
                                label="Profesor"
                                required
                                maxVisibleOptions={3}
                                options={teacherOptions}
                                value={courseSelected?.teacher?._id ? String(courseSelected.teacher._id) : ''}
                                onChange={(nextValue) => {
                                    clearFieldError('teacher');
                                    if (!nextValue) {
                                        setCourseSelected((prev) => ({ ...prev, teacher: '' }));
                                        return;
                                    }
                                    const teacher = teachers.find((item) => String(item._id) === nextValue);
                                    if (!teacher) return;
                                    setCourseSelected((prev) => ({
                                        ...prev,
                                        teacher: {
                                            _id: teacher._id,
                                            name: teacher.name,
                                            description: teacher.description,
                                            email: teacher.email || '',
                                        },
                                    }));
                                }}
                                placeholder="Seleccionar profesor"
                                searchPlaceholder="Buscar profesor..."
                                error={formErrors.teacher}
                            />

                            <SearchableSelect
                                label="Moderador del grupo"
                                required
                                maxVisibleOptions={3}
                                options={moderatorOptions}
                                value={courseSelected?.moderator?._id ? String(courseSelected.moderator._id) : ''}
                                onChange={(nextValue) => {
                                    clearFieldError('moderator');
                                    if (!nextValue) {
                                        setCourseSelected((prev) => ({ ...prev, moderator: '' }));
                                        return;
                                    }
                                    const moderator = adminUsers.find((item) => String(item._id) === nextValue);
                                    if (!moderator) return;
                                    setCourseSelected((prev) => ({
                                        ...prev,
                                        moderator: {
                                            _id: moderator._id,
                                            name: moderator.name,
                                            description: moderator.description,
                                            email: moderator.email || '',
                                        },
                                    }));
                                }}
                                placeholder="Seleccionar moderador"
                                searchPlaceholder="Buscar moderador..."
                                error={formErrors.moderator}
                            />
                        </CourseFormGrid>
                    </CourseFormSection>

                    {canManageZoom && (
                        <CourseFormSection
                            id="course-section-zoom"
                            title="Clases en vivo"
                            description="Configura si el grupo usará videollamadas con Zoom."
                            icon={<VideoCameraIcon className={sectionIconClass} />}
                        >
                            <InputSwitch
                                label="Videollamadas con Zoom"
                                enabled={courseSelected?.zoomEnabled === true}
                                setEnabled={(value) => setCourseSelected((prev) => ({
                                    ...prev,
                                    zoomEnabled: value === true,
                                }))}
                            />
                            <p className="text-xs text-gray-500">
                                Puedes activarlo o desactivarlo en cualquier momento.
                            </p>
                        </CourseFormSection>
                    )}

                    <div className="rounded-lg border border-indigo-100 bg-white px-5 py-3 shadow-sm">
                        <p className="text-sm text-indigo-900">
                            Los campos marcados con
                            {' '}
                            <span className="font-semibold text-red-500">*</span>
                            {' '}
                            son obligatorios para crear o editar un curso.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!userCanManageCourses) {
        return null;
    }

    if (isEdit && isOpen && (!course || String(course._id) !== String(courseId))) {
        return (
            <DrawerNoHeader
                isOpen={isOpen}
                toggleDrawer={handleToggleDrawer}
                lockClose={isProcessing}
                panelClassName="overflow-hidden rounded-xl border border-slate-200/80 shadow-lg shadow-slate-900/10"
                sizeMD="70%"
                sizeLG="55%"
            >
                <div className="p-8 text-sm text-gray-500">Cargando curso…</div>
            </DrawerNoHeader>
        );
    }

    return (
        <DrawerNoHeader
            isOpen={isOpen}
            toggleDrawer={handleToggleDrawer}
            lockClose={isProcessing}
            panelClassName="overflow-hidden rounded-xl border border-slate-200/80 shadow-lg shadow-slate-900/10"
            direction="right"
            sizeMD="70%"
            sizeLG="55%"
            sizeXL="50%"
            sizeXXL="45%"
            footer={(
                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={handleCancel} disabled={isProcessing}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        loading={isProcessing}
                        loadingLabel={isEdit ? 'Guardando…' : 'Creando…'}
                    >
                        {isEdit ? 'Guardar cambios' : 'Crear curso'}
                    </Button>
                </div>
            )}
        >
            {drawerBody}
        </DrawerNoHeader>
    );
};
